import React, { createContext, useContext, useState, useEffect } from 'react';
import supabase from '../supabase/supabase';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const fetchSubscriptionAndSetUser = async (sessionUser) => {
      if (!sessionUser) {
        if (mounted) setUser(null);
        return;
      }

      try {
        // Tier comes from the server-side resolver (get_my_tier), the single
        // source of truth across all subscription rows (paid, comp, LTD,
        // hand-granted beta). Never derived client-side: picking "the newest
        // subscription row" breaks the moment a user holds more than one.
        const [tierRes, profRes] = await Promise.all([
          supabase.rpc('get_my_tier'),
          supabase
            .from('profiles')
            .select('role')
            .eq('id', sessionUser.id)
            .maybeSingle()
        ]);

        const tier = tierRes.data?.tier || 'free';
        const isPro = tierRes.data?.is_pro || false; // pro OR beta entitlement
        const role = profRes.data?.role || 'user';

        if (mounted) {
          setUser((prev) => {
            // Prevent object reference change if nothing relevant changed
            if (prev && prev.id === sessionUser.id && prev.is_pro === isPro && prev.tier === tier && prev.role === role) {
              return prev;
            }
            return { ...sessionUser, is_pro: isPro, tier, role };
          });
        }
      } catch (err) {
        console.error('Error fetching user context data:', err);
        if (mounted) {
          setUser((prev) => {
            if (prev && prev.id === sessionUser.id) return prev;
            return { ...sessionUser, is_pro: false, tier: 'free', role: 'user' };
          });
        }
      }
    };

    // 1. Fetch the initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      fetchSubscriptionAndSetUser(session?.user).finally(() => {
        if (mounted) setLoading(false);
      });
    });

    // 2. Listen for auth state changes (login, logout, token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        fetchSubscriptionAndSetUser(session?.user);
      }
    );

    // Cleanup subscription on unmount
    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Realtime listener to update the context immediately if subscription changes in the database
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel('auth_subscription_changes')
      .on(
        'postgres',
        { event: '*', schema: 'public', table: 'subscriptions', filter: `user_id=eq.${user.id}` },
        async () => {
          // Any subscription change: re-ask the server-side resolver rather
          // than recomputing locally from one row (the user may hold several —
          // comp + paid, LTD + comp — and only the resolver sees them all).
          const { data } = await supabase.rpc('get_my_tier');
          if (data) {
            setUser((prev) => (prev ? { ...prev, is_pro: data.is_pro, tier: data.tier } : prev));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  const signUp = async (email, password) => {
    // Inject the referral code if it exists in localStorage
    const referredBy = localStorage.getItem('mot_ref');
    const options = { data: {} };
    if (referredBy) {
      options.data.referred_by = referredBy;
    }

    const res = await supabase.auth.signUp({ email, password, options });
    
    // Clear the referral code so it isn't accidentally reused
    if (!res.error && referredBy) {
      localStorage.removeItem('mot_ref');
    }
    return res;
  };

  const signIn = async (email, password) => {
    return supabase.auth.signInWithPassword({
      email,
      password,
    });
  };

  const signOut = async () => {
    return supabase.auth.signOut();
  };

const handleCheckout = async (planType) => {
  if (!user?.id) return;
  if (!["monthly", "yearly"].includes(planType)) return;

  const { data: { session }, error: sessionError } = await supabase.auth.getSession();

  if (sessionError || !session?.access_token) {
    console.error("No active session for checkout:", sessionError);
    return;
  }

  const { data, error } = await supabase.functions.invoke("create-checkout-session", {
    body: { planType },
    headers: {
      Authorization: `Bearer ${session.access_token}`,
    },
  });

  if (error) {
    console.error("Checkout session creation failed:", error);
    return;
  }

  if (data?.checkout_url) {
    window.location.href = data.checkout_url;
  } else {
    console.error("Missing checkout_url in response:", data);
  }
};

  const value = {
    user,
    loading,
    signUp,
    signIn,
    signOut,
    handleCheckout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};